"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { START_FEN, applyUci, gameResult, replay, type GameResult, type PlayedMove } from "@/lib/chess/game";
import type { GameReview } from "@/lib/chess/review";
import { getLevel } from "@/lib/chess/bot";

/** Standard Elo update with K=32. `score` is 1, 0.5 or 0 for the player. */
export function eloUpdate(rating: number, opponent: number, score: number, k = 32): number {
  const expected = 1 / (1 + Math.pow(10, (opponent - rating) / 400));
  return Math.round(rating + k * (score - expected));
}

function rated(s: { hintsUsed: number; takebacks: number }) {
  return s.hintsUsed === 0 && s.takebacks === 0;
}

/**
 * How many moves remain after a takeback: back to the player's turn, one ply if the bot
 * has not answered yet, else two. Null when there is nothing to take back, such as the
 * bot's opening move when the player has Black.
 */
export function takebackLength(s: Pick<PlayState, "startFen" | "moves" | "playerColor">): number | null {
  const blackFirst = s.startFen.split(" ")[1] === "b";
  const playerToMoveAfter = (len: number) => ((len % 2 === 0) !== blackFirst ? "w" : "b") === s.playerColor;
  for (let len = s.moves.length - 1; len >= 0; len--) if (playerToMoveAfter(len)) return len;
  return null;
}

function settle(s: PlayState, result: GameResult): Partial<PlayState> {
  if (!rated(s)) return { lastRatingChange: null };
  const score = result.winner === null ? 0.5 : result.winner === s.playerColor ? 1 : 0;
  const rating = eloUpdate(s.rating, getLevel(s.levelId).rating, score);
  return { rating, lastRatingChange: rating - s.rating };
}

export type PlayState = {
  gameId: string;
  startFen: string;
  moves: PlayedMove[];
  playerColor: "w" | "b";
  levelId: string;
  status: "setup" | "playing" | "over";
  result: GameResult | null;
  view: "3d" | "2d";
  /** Ply being viewed: null follows the live position. */
  cursor: number | null;
  hintsUsed: number;
  takebacks: number;
  review: GameReview | null;
  rating: number;
  /** Rating change from the last finished game, or null if it was unrated. */
  lastRatingChange: number | null;

  newGame: (color: "w" | "b" | "random", levelId: string) => void;
  play: (uci: string) => PlayedMove | null;
  takeback: () => void;
  resign: () => void;
  setView: (v: "3d" | "2d") => void;
  setCursor: (c: number | null) => void;
  setReview: (r: GameReview | null) => void;
  setRating: (r: number) => void;
  noteHint: () => void;
  backToSetup: () => void;
};

export const usePlayStore = create<PlayState>()(
  persist(
    (set, get) => ({
      gameId: "legacy",
      startFen: START_FEN,
      moves: [],
      playerColor: "w",
      levelId: "bishop",
      status: "setup",
      result: null,
      view: "3d",
      cursor: null,
      hintsUsed: 0,
      takebacks: 0,
      review: null,
      rating: 1200,
      lastRatingChange: null,

      newGame: (color, levelId) =>
        set({
          // randomUUID is missing on plain-http origins such as a LAN dev server.
          gameId: crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
          startFen: START_FEN,
          moves: [],
          playerColor: color === "random" ? (Math.random() < 0.5 ? "w" : "b") : color,
          levelId,
          status: "playing",
          result: null,
          cursor: null,
          hintsUsed: 0,
          takebacks: 0,
          review: null,
          lastRatingChange: null,
        }),

      play: (uci) => {
        const s = get();
        if (s.status !== "playing") return null;
        const c = replay(s.startFen, s.moves.map((m) => m.uci));
        const rec = applyUci(c, uci);
        if (!rec) return null;
        const result = gameResult(c);
        set({ moves: [...s.moves, rec], cursor: null, status: result ? "over" : "playing", result, ...(result ? settle(s, result) : {}) });
        return rec;
      },

      takeback: () => {
        const s = get();
        const len = takebackLength(s);
        if (len === null) return;
        set({ moves: s.moves.slice(0, len), status: "playing", result: null, cursor: null, takebacks: s.takebacks + 1, review: null });
      },

      resign: () => {
        const s = get();
        if (s.status !== "playing") return;
        const result: GameResult = { winner: s.playerColor === "w" ? "b" : "w", reason: "resignation" };
        set({ status: "over", result, ...settle(s, result) });
      },

      setView: (view) => set({ view }),
      setCursor: (cursor) => set({ cursor: cursor === null ? null : Math.max(0, Math.min(get().moves.length, Math.trunc(cursor))) }),
      setReview: (review) => set({ review }),
      setRating: (rating) => set({ rating }),
      noteHint: () => set({ hintsUsed: get().hintsUsed + 1 }),
      backToSetup: () => set({ status: "setup", cursor: null }),
    }),
    { name: "atlas:play", version: 1, skipHydration: true },
  ),
);
